import { toEmbedTarget } from "@growth/editor";
import { cn, PromptDialog } from "@growth/ui";
import { type Editor } from "@tiptap/react";
import {
  Bold,
  ChartColumn,
  Code,
  Heading2,
  Heading3,
  Heading4,
  ImagePlus,
  ImageUpscale,
  Italic,
  Link2,
  List,
  ListOrdered,
  LoaderCircle,
  type LucideIcon,
  Minus,
  Quote,
  Redo2,
  SquareCode,
  Strikethrough,
  Underline,
  Undo2,
  Youtube,
} from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";

import { generateImage } from "../../lib/ai";
import { apiErrorMessage } from "../../lib/api-error";
import { ACCEPTED_IMAGE_TYPES, uploadImage } from "../../lib/media";
import { ChartDialog } from "./chart-dialog";

type ToolProps = {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

const Tool = ({ icon: Icon, label, active, disabled, onClick }: ToolProps) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    className={cn(
      "flex size-8 items-center justify-center rounded-md text-muted transition-colors",
      "hover:bg-paper hover:text-ink disabled:pointer-events-none disabled:opacity-40",
      active && "bg-ink text-white hover:bg-ink hover:text-white",
    )}
  >
    <Icon className="size-4" />
  </button>
);

const Divider = () => <span className="mx-1 h-5 w-px bg-line" aria-hidden />;

const normalizeHref = (value: string): string | null => {
  if (!value) return null;
  const href =
    /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith("/") ? value : `https://${value}`;
  try {
    const url = new URL(href, "https://example.com");
    return ["http:", "https:", "mailto:"].includes(url.protocol) ? href : null;
  } catch {
    return null;
  }
};

type Dialog = "link" | "embed" | "alt" | "chart" | "ai-image" | null;

type ToolbarProps = {
  editor: Editor;
  onError: (message: string) => void;
  /** Post title, for AI image context. */
  title: string;
  aiEnabled: boolean;
};

export const Toolbar = ({ editor, onError, title, aiEnabled }: ToolbarProps) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingImage, setPendingImage] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  /** Text just before the cursor: what an inline image should illustrate. */
  const nearbyText = () => {
    const { from } = editor.state.selection;
    return editor.state.doc.textBetween(Math.max(0, from - 900), from, " ").slice(-900);
  };

  const chain = () => editor.chain().focus();

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setPendingImage(url);
      setDialog("alt");
    } catch (error) {
      onError(apiErrorMessage(error, error instanceof Error ? error.message : "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-line bg-white/95 px-2 py-1.5 backdrop-blur">
      <Tool
        icon={Heading2}
        label="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => chain().toggleHeading({ level: 2 }).run()}
      />
      <Tool
        icon={Heading3}
        label="Heading 3"
        active={editor.isActive("heading", { level: 3 })}
        onClick={() => chain().toggleHeading({ level: 3 }).run()}
      />
      <Tool
        icon={Heading4}
        label="Heading 4"
        active={editor.isActive("heading", { level: 4 })}
        onClick={() => chain().toggleHeading({ level: 4 }).run()}
      />
      <Divider />
      <Tool
        icon={Bold}
        label="Bold"
        active={editor.isActive("bold")}
        onClick={() => chain().toggleBold().run()}
      />
      <Tool
        icon={Italic}
        label="Italic"
        active={editor.isActive("italic")}
        onClick={() => chain().toggleItalic().run()}
      />
      <Tool
        icon={Underline}
        label="Underline"
        active={editor.isActive("underline")}
        onClick={() => chain().toggleUnderline().run()}
      />
      <Tool
        icon={Strikethrough}
        label="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => chain().toggleStrike().run()}
      />
      <Tool
        icon={Code}
        label="Inline code"
        active={editor.isActive("code")}
        onClick={() => chain().toggleCode().run()}
      />
      <Tool
        icon={Link2}
        label="Link"
        active={editor.isActive("link")}
        onClick={() => setDialog("link")}
      />
      <Divider />
      <Tool
        icon={List}
        label="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => chain().toggleBulletList().run()}
      />
      <Tool
        icon={ListOrdered}
        label="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => chain().toggleOrderedList().run()}
      />
      <Tool
        icon={Quote}
        label="Quote"
        active={editor.isActive("blockquote")}
        onClick={() => chain().toggleBlockquote().run()}
      />
      <Tool
        icon={SquareCode}
        label="Code block"
        active={editor.isActive("codeBlock")}
        onClick={() => chain().toggleCodeBlock().run()}
      />
      <Tool icon={Minus} label="Divider" onClick={() => chain().setHorizontalRule().run()} />
      <Divider />
      <Tool
        icon={uploading ? LoaderCircle : ImagePlus}
        label={uploading ? "Uploading…" : "Image"}
        disabled={uploading}
        onClick={() => fileInput.current?.click()}
      />
      <Tool icon={Youtube} label="YouTube or Loom video" onClick={() => setDialog("embed")} />
      {aiEnabled && (
        <>
          <Divider />
          <Tool
            icon={ChartColumn}
            label="Chart or dashboard (AI)"
            onClick={() => setDialog("chart")}
          />
          <Tool
            icon={generating ? LoaderCircle : ImageUpscale}
            label={generating ? "Generating image…" : "Generate an image here (AI)"}
            disabled={generating}
            onClick={() => setDialog("ai-image")}
          />
        </>
      )}
      <span className="flex-1" />
      <Tool
        icon={Undo2}
        label="Undo"
        disabled={!editor.can().undo()}
        onClick={() => chain().undo().run()}
      />
      <Tool
        icon={Redo2}
        label="Redo"
        disabled={!editor.can().redo()}
        onClick={() => chain().redo().run()}
      />

      <input
        ref={fileInput}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        onChange={onFile}
      />

      <PromptDialog
        open={dialog === "link"}
        onOpenChange={(open) => !open && setDialog(null)}
        title={editor.isActive("link") ? "Edit link" : "Add link"}
        label="URL"
        placeholder="https://…"
        initialValue={(editor.getAttributes("link")["href"] as string | undefined) ?? ""}
        submitLabel="Apply"
        secondaryAction={
          editor.isActive("link")
            ? {
                label: "Remove link",
                onClick: () => chain().extendMarkRange("link").unsetLink().run(),
              }
            : undefined
        }
        onSubmit={(value) => {
          const href = normalizeHref(value);
          if (!href) return "Enter a web address (https://…) or mailto: link";
          if (editor.state.selection.empty && !editor.isActive("link")) {
            chain()
              .insertContent({
                type: "text",
                text: value,
                marks: [{ type: "link", attrs: { href } }],
              })
              .run();
          } else {
            chain().extendMarkRange("link").setLink({ href }).run();
          }
          return null;
        }}
      />
      <PromptDialog
        open={dialog === "embed"}
        onOpenChange={(open) => !open && setDialog(null)}
        title="Embed a video"
        label="YouTube or Loom link"
        placeholder="https://www.youtube.com/watch?v=…"
        submitLabel="Embed"
        onSubmit={(value) => {
          if (!toEmbedTarget(value)) return "Paste a YouTube or Loom share link";
          chain().setEmbed(value).run();
          return null;
        }}
      />
      <ChartDialog
        open={dialog === "chart"}
        onOpenChange={(open) => !open && setDialog(null)}
        onInsert={(spec) => chain().insertWidget(spec).run()}
      />
      <PromptDialog
        open={dialog === "ai-image"}
        onOpenChange={(open) => !open && setDialog(null)}
        title="Generate an image here"
        label="What should it show? (optional — leave empty to illustrate the text above)"
        placeholder="e.g. a candidate's notes on a desk before a panel interview"
        submitLabel="Generate"
        onSubmit={(direction) => {
          const position = editor.state.selection.from;
          setGenerating(true);
          generateImage({
            purpose: "inline",
            context: `${title}\n${nearbyText()}`.trim() || title || "Blog illustration",
            ...(direction ? { direction } : {}),
          })
            .then(({ url, alt }) =>
              editor
                .chain()
                .focus()
                .insertContentAt(position, { type: "image", attrs: { src: url, alt } })
                .run(),
            )
            .catch((error) => onError(apiErrorMessage(error, "Image generation failed")))
            .finally(() => setGenerating(false));
          return null;
        }}
      />
      <PromptDialog
        open={dialog === "alt"}
        onOpenChange={(open) => {
          if (open) return;
          // Closing without a description still inserts the image, with empty alt.
          if (pendingImage) chain().setImage({ src: pendingImage, alt: "" }).run();
          setPendingImage(null);
          setDialog(null);
        }}
        title="Describe the image"
        label="Alt text (for screen readers and search)"
        placeholder="e.g. Candidate practising an interview on a laptop"
        submitLabel="Insert image"
        onSubmit={(alt) => {
          if (pendingImage) chain().setImage({ src: pendingImage, alt }).run();
          setPendingImage(null);
          return null;
        }}
      />
    </div>
  );
};
